import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Modal, Fade, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TerminalIcon from '@mui/icons-material/Terminal';
import { playKeySound, playClickSound, playChimeSound } from '../../utils/soundEffects';

const BANNER = `
  ◆━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━◆
    G I R I S H   K U M A R   G O Y A L
    [ Systems Engineer · Candidate Master (2013) · Independent Researcher ]
  ◆━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━◆
    • Arch Linux x86_64   • POSIX Web Terminal   • epsilon_xd
    • Active Book: "Biology through Curiosity : Questions that became Knowledge"
  ────────────────────────────────────────────────────────────────────────
    Type 'help' for commands. Try 'cf', 'projects', 'books', 'skills', 'about'.
`;

const COMMAND_LIST = [
  { cmd: 'help', desc: 'Display all available terminal commands & syntax' },
  { cmd: 'about', desc: 'Full biography, academic background & intellectual pillars' },
  { cmd: 'cf', desc: 'Query live Codeforces Candidate Master metrics (2013 max rating)' },
  { cmd: 'projects', desc: 'Inspect active SaaS systems, algorithms, and POSIX C projects' },
  { cmd: 'books', desc: 'Display personal bookshelf catalogue & in-progress book manuscript' },
  { cmd: 'skills', desc: 'List programming languages, tools, frameworks & Linux distros' },
  { cmd: 'contact', desc: 'Show official communication coordinates & verified social links' },
  { cmd: 'cat [file]', desc: 'Read file contents (e.g. cat bio, cat projects, cat cf)' },
  { cmd: 'ls', desc: 'List virtual directories and system files' },
  { cmd: 'theme', desc: 'Toggle website aesthetic between Midnight Dark and Ivory Light' },
  { cmd: 'matrix', desc: 'Execute the Matrix digital green rain visual stream' },
  { cmd: 'clear', desc: 'Clear the terminal session buffer' },
  { cmd: 'exit', desc: 'Close and terminate current web shell session' },
];

const UnixShellModal = ({ open, onClose, toggleTheme, mode }) => {
  const [history, setHistory] = useState([
    { type: 'banner', content: BANNER },
  ]);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isMatrix, setIsMatrix] = useState(false);

  const inputRef = useRef(null);
  const terminalEndRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, isMatrix]);

  const handleCommand = (rawCmd) => {
    const trimmed = rawCmd.trim();
    if (!trimmed) {
      setHistory(prev => [...prev, { type: 'input', content: '' }]);
      return;
    }

    playClickSound();
    setCmdHistory(prev => [trimmed, ...prev]);
    setHistoryIndex(-1);

    const parts = trimmed.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    let output = '';
    let isHtml = false;

    if (cmd === 'echo') {
      output = args.join(' ');
      setHistory(prev => [
        ...prev,
        { type: 'input', content: rawCmd },
        { type: 'output', content: output, isHtml: false },
      ]);
      return;
    }

    switch (cmd) {
      case 'help':
      case 'commands':
        output = (
          <Box sx={{ my: 1.5 }}>
            <Typography sx={{ color: '#38bdf8', fontWeight: 800, mb: 1, letterSpacing: '0.08em' }}>
              ══════ SYSTEM COMMAND DIRECTORY ══════
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 0.8 }}>
              {COMMAND_LIST.map(c => (
                <React.Fragment key={c.cmd}>
                  <Typography sx={{ color: '#fbbf24', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace' }}>
                    {`$ ${c.cmd}`}
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                    {c.desc}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
        isHtml = true;
        break;

      case 'ls':
        output = (
          <Box sx={{ my: 1, display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#38bdf8', fontWeight: 700 }}>📁 projects/</Typography>
            <Typography sx={{ color: '#38bdf8', fontWeight: 700 }}>📁 writing/</Typography>
            <Typography sx={{ color: '#38bdf8', fontWeight: 700 }}>📁 library/</Typography>
            <Typography sx={{ color: '#34d399' }}>📄 bio.txt</Typography>
            <Typography sx={{ color: '#34d399' }}>📄 codeforces.json</Typography>
            <Typography sx={{ color: '#34d399' }}>📄 skills.md</Typography>
            <Typography sx={{ color: '#fbbf24' }}>⚙️ config.sys</Typography>
          </Box>
        );
        isHtml = true;
        break;

      case 'cat': {
        const target = (args[0] || '').toLowerCase();
        if (!target) {
          output = "usage: cat <file> (e.g. cat bio, cat projects, cat cf, cat skills)";
        } else if (target.includes('bio') || target.includes('about')) {
          handleCommand('about');
          return;
        } else if (target.includes('cf') || target.includes('codeforces')) {
          handleCommand('cf');
          return;
        } else if (target.includes('proj')) {
          handleCommand('projects');
          return;
        } else if (target.includes('skill')) {
          handleCommand('skills');
          return;
        } else if (target.includes('book') || target.includes('writ')) {
          handleCommand('books');
          return;
        } else {
          output = `cat: ${args[0]}: No such file or directory. Try 'ls' to list files.`;
        }
        break;
      }

      case 'about':
      case 'bio':
      case 'whoami':
        output = (
          <Box sx={{ my: 1.5, p: 2, borderRadius: '10px', backgroundColor: 'rgba(56, 189, 248, 0.05)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography sx={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.98rem' }}>
                GIRISH KUMAR GOYAL
              </Typography>
              <Chip label="B.TECH CSE (2022)" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }} />
            </Box>
            <Typography sx={{ color: '#cbd5e1', lineHeight: 1.65, fontSize: '0.86rem', mb: 1.2 }}>
              Software engineer, systems programmer, and independent researcher. Focusing on distributed systems, multi-tenant cloud platforms, algorithmic complexity proofs, and biological systems modeling.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: '0.8rem' }}>
              <Typography sx={{ color: '#fbbf24' }}>📍 Bulandshahr, UP, India</Typography>
              <Typography sx={{ color: '#34d399' }}>⚡ Codeforces: Candidate Master</Typography>
              <Typography sx={{ color: '#c084fc' }}>📖 Author: Biology Manuscript</Typography>
            </Box>
          </Box>
        );
        isHtml = true;
        break;

      case 'cf':
      case 'codeforces':
        output = (
          <Box sx={{ my: 1.5, p: 2, border: '1px solid rgba(52, 211, 153, 0.35)', borderRadius: '10px', backgroundColor: 'rgba(52, 211, 153, 0.05)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#34d399', fontWeight: 800, fontSize: '0.98rem' }}>
                HANDLE: epsilon_xd
              </Typography>
              <Chip label="LIVE TELEMETRY" size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 800, backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: '1px solid #34d399' }} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1 }}>
              <Typography sx={{ color: '#38bdf8' }}>RANK: <Box component="span" sx={{ fontWeight: 800 }}>Candidate Master</Box></Typography>
              <Typography sx={{ color: '#fbbf24' }}>MAX RATING: <Box component="span" sx={{ fontWeight: 800 }}>2013</Box></Typography>
              <Typography sx={{ color: '#cbd5e1' }}>CURRENT RATING: <Box component="span" sx={{ fontWeight: 800 }}>1971</Box></Typography>
              <Typography sx={{ color: '#a5b4fc' }}>SOLVED: <Box component="span" sx={{ fontWeight: 800 }}>1,000+ Problems</Box></Typography>
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.08)', pt: 0.8 }}>
              Specialization in Graph Theory, Dynamic Programming, Segment Trees, and Asymptotic Optimization.
            </Typography>
          </Box>
        );
        isHtml = true;
        break;

      case 'projects':
        output = (
          <Box sx={{ my: 1.5 }}>
            <Typography sx={{ color: '#38bdf8', fontWeight: 800, mb: 1 }}>
              ══════ SOFTWARE SYSTEMS ARCHIVE ══════
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <Box sx={{ p: 1.5, borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.2)', backgroundColor: 'rgba(56, 189, 248, 0.03)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: '#fbbf24', fontWeight: 700 }}>1. Multi-Tenant SaaS College Coding Club Platform</Typography>
                  <Chip label="IN PROGRESS // 2024" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.15)' }} />
                </Box>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.3 }}>Stack: React.js, Node.js, TypeScript, PostgreSQL, Docker, Tailwind CSS</Typography>
              </Box>

              <Box sx={{ p: 1.5, borderRadius: '8px', border: '1px solid rgba(52, 211, 153, 0.2)', backgroundColor: 'rgba(52, 211, 153, 0.03)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: '#34d399', fontWeight: 700 }}>2. DSA Reference Handbook</Typography>
                  <Chip label="PUBLISHED // 2023" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: '#34d399', backgroundColor: 'rgba(52, 211, 153, 0.15)' }} />
                </Box>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.3 }}>Stack: LaTeX, C++20, TikZ Vector Geometry Engine</Typography>
              </Box>

              <Box sx={{ p: 1.5, borderRadius: '8px', border: '1px solid rgba(192, 132, 252, 0.2)', backgroundColor: 'rgba(192, 132, 252, 0.03)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: '#c084fc', fontWeight: 700 }}>3. POSIX Minimalist Command Shell</Typography>
                  <Chip label="COMPLETED // 2022" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, color: '#c084fc', backgroundColor: 'rgba(192, 132, 252, 0.15)' }} />
                </Box>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.3 }}>Stack: C, Linux POSIX System Calls, fork(), execvp(), waitpid()</Typography>
              </Box>
            </Box>
          </Box>
        );
        isHtml = true;
        break;

      case 'books':
      case 'writing':
        output = (
          <Box sx={{ my: 1.5, p: 2, borderRadius: '10px', backgroundColor: 'rgba(192, 132, 252, 0.05)', border: '1px solid rgba(192, 132, 252, 0.3)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
              <Typography sx={{ color: '#c084fc', fontWeight: 800, fontSize: '0.96rem' }}>
                ACTIVE MANUSCRIPT // IN PROGRESS
              </Typography>
            </Box>
            <Typography sx={{ color: '#fb7185', fontWeight: 700, fontSize: '0.94rem', mb: 0.5 }}>
              "Biology through Curiosity : Questions that became Knowledge"
            </Typography>
            <Typography sx={{ color: '#cbd5e1', fontSize: '0.84rem', lineHeight: 1.6, mb: 1.5 }}>
              A long-form intellectual exploration synthesizing molecular genetics, evolutionary game theory, cellular automata, and the mechanics of scientific curiosity into structured knowledge.
            </Typography>
            <Typography sx={{ color: '#38bdf8', fontWeight: 700, fontSize: '0.82rem' }}>
              DIGITAL BOOKSHELF PILLARS:
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
              Computing Architecture · Geopolitics & Statecraft · Indian Classical Thought · Evolutionary Biology
            </Typography>
          </Box>
        );
        isHtml = true;
        break;

      case 'skills':
        output = (
          <Box sx={{ my: 1.5 }}>
            <Typography sx={{ color: '#38bdf8', fontWeight: 800, mb: 1 }}>
              ══════ TECHNICAL TOOLKIT & ARCHITECTURE ══════
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 1 }}>
              <Typography sx={{ color: '#34d399', fontWeight: 700 }}>LANGUAGES:</Typography>
              <Typography sx={{ color: '#f8fafc' }}>C, C++, Python, JavaScript, TypeScript, Java, SQL, Go</Typography>

              <Typography sx={{ color: '#38bdf8', fontWeight: 700 }}>FRAMEWORKS:</Typography>
              <Typography sx={{ color: '#f8fafc' }}>React.js, Angular, Node.js, PostgreSQL, Docker, Kubernetes, LaTeX</Typography>

              <Typography sx={{ color: '#c084fc', fontWeight: 700 }}>OPERATING SYSTEMS:</Typography>
              <Typography sx={{ color: '#f8fafc' }}>Arch Linux, Debian, Ubuntu, Kali Linux, Parrot OS, Pop!_OS</Typography>

              <Typography sx={{ color: '#fb7185', fontWeight: 700 }}>ANALYTICAL DOMAINS:</Typography>
              <Typography sx={{ color: '#f8fafc' }}>Algorithms, Data Structures, Evolutionary Genetics, Systems Modeling</Typography>
            </Box>
          </Box>
        );
        isHtml = true;
        break;

      case 'contact':
      case 'email':
        output = (
          <Box sx={{ my: 1.5, p: 1.5, borderRadius: '8px', border: '1px solid rgba(229, 169, 60, 0.3)', backgroundColor: 'rgba(229, 169, 60, 0.05)' }}>
            <Typography sx={{ color: '#fbbf24', fontWeight: 800, mb: 0.8 }}>COMMUNICATION COORDINATES:</Typography>
            <Typography sx={{ color: '#f8fafc', mb: 0.4 }}>• Email: <Box component="span" sx={{ color: '#38bdf8', fontWeight: 700 }}>gk.goyal.0.03@gmail.com</Box></Typography>
            <Typography sx={{ color: '#f8fafc', mb: 0.4 }}>• LinkedIn: <Box component="span" sx={{ color: '#38bdf8' }}>linkedin.com/in/girish-kumar-goyal-189152198</Box></Typography>
            <Typography sx={{ color: '#f8fafc', mb: 0.4 }}>• GitHub: <Box component="span" sx={{ color: '#34d399' }}>github.com/Girish-1-Goyal</Box></Typography>
            <Typography sx={{ color: '#f8fafc' }}>• Codeforces: <Box component="span" sx={{ color: '#fbbf24' }}>codeforces.com/profile/epsilon_xd</Box></Typography>
          </Box>
        );
        isHtml = true;
        break;

      case 'theme':
        toggleTheme();
        output = `Theme switched to ${mode === 'dark' ? 'Ivory Light' : 'Midnight Dark'} mode.`;
        break;

      case 'matrix':
        setIsMatrix(true);
        playChimeSound();
        output = 'Matrix protocol initiated. Wake up, Neo... (Press clear to reset terminal)';
        break;

      case 'clear':
      case 'cls':
        setHistory([]);
        setIsMatrix(false);
        return;

      case 'exit':
      case 'quit':
        onClose();
        return;

      case 'date':
        output = new Date().toUTCString();
        break;

      case 'uname':
        output = 'Linux archlinux 6.8.9-arch1-1 #1 SMP PREEMPT_DYNAMIC x86_64 GNU/Linux';
        break;

      case 'sudo':
        output = 'Permission denied: Incident will be logged in security audit.';
        break;

      default:
        output = `command not found: '${cmd}'. Type 'help' to see list of valid commands.`;
        break;
    }

    setHistory(prev => [
      ...prev,
      { type: 'input', content: rawCmd },
      { type: 'output', content: output, isHtml },
    ]);
  };

  const handleKeyDown = (e) => {
    playKeySound();

    if (e.key === 'Enter') {
      handleCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (cmdHistory.length > 0) {
        const nextIdx = Math.min(historyIndex + 1, cmdHistory.length - 1);
        setHistoryIndex(nextIdx);
        setInput(cmdHistory[nextIdx]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const prevIdx = historyIndex - 1;
        setHistoryIndex(prevIdx);
        setInput(cmdHistory[prevIdx]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1.5, sm: 2 },
        backdropFilter: 'blur(16px)',
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            width: { xs: '96vw', sm: 840, md: 890 },
            height: { xs: '82vh', sm: 530, md: 570 },
            backgroundColor: isMatrix ? '#020d05' : '#07090e',
            border: `1px solid ${isMatrix ? '#00ff66' : 'rgba(229, 169, 60, 0.35)'}`,
            borderRadius: '16px',
            boxShadow: isMatrix 
              ? '0 0 50px rgba(0, 255, 102, 0.4)'
              : '0 24px 60px rgba(0, 0, 0, 0.8), 0 0 28px rgba(229, 169, 60, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: '"JetBrains Mono", monospace',
            color: isMatrix ? '#00ff66' : '#f8fafc',
            transition: 'all 0.25s ease',
          }}
        >
          {/* Terminal Titlebar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.2,
              py: 1.3,
              backgroundColor: isMatrix ? '#011508' : '#0c0f17',
              borderBottom: `1px solid ${isMatrix ? '#00ff6644' : 'rgba(255, 255, 255, 0.08)'}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TerminalIcon sx={{ fontSize: 18, color: isMatrix ? '#00ff66' : '#e5a93c' }} />
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.84rem', fontWeight: 800, letterSpacing: '0.04em' }}>
                girish@archlinux:~ (web-shell) [POSIX v3.0]
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Terminal Screen Body */}
          <Box
            onClick={() => inputRef.current?.focus()}
            sx={{
              flex: 1,
              p: { xs: 1.5, sm: 2.5 },
              overflowY: 'auto',
              cursor: 'text',
              fontSize: { xs: '0.82rem', sm: '0.86rem' },
              lineHeight: 1.55,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {history.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                {item.type === 'banner' && (
                  <pre style={{ margin: 0, color: isMatrix ? '#00ff66' : '#e5a93c', fontSize: '0.72rem', overflowX: 'auto', lineHeight: 1.35, fontWeight: 700 }}>
                    {item.content}
                  </pre>
                )}
                {item.type === 'input' && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography component="span" sx={{ color: isMatrix ? '#00ff66' : '#38bdf8', fontWeight: 800, fontSize: 'inherit' }}>
                      girish@archlinux:~$
                    </Typography>
                    <Typography component="span" sx={{ color: isMatrix ? '#00ff66' : '#ffffff', fontSize: 'inherit' }}>
                      {item.content}
                    </Typography>
                  </Box>
                )}
                {item.type === 'output' && (
                  <Box sx={{ pl: 1.5, color: isMatrix ? '#00ff66' : '#cbd5e1' }}>
                    {item.isHtml ? item.content : item.content}
                  </Box>
                )}
              </Box>
            ))}

            {/* Live Prompt Line */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Typography component="span" sx={{ color: isMatrix ? '#00ff66' : '#38bdf8', fontWeight: 800, fontSize: 'inherit', whiteSpace: 'nowrap' }}>
                girish@archlinux:~$
              </Typography>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: isMatrix ? '#00ff66' : '#ffffff',
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 'inherit',
                  flex: 1,
                  padding: 0,
                  caretColor: isMatrix ? '#00ff66' : '#e5a93c',
                }}
                autoFocus
                spellCheck="false"
                autoComplete="off"
              />
            </Box>
            <div ref={terminalEndRef} />
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default UnixShellModal;
